import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { suppliesApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, PencilSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const Supplies = () => {
  const { t, language } = useLanguage();
  const [supplies, setSupplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSupply, setSelectedSupply] = useState(null);

  const [formData, setFormData] = useState({
    designation: '',
    quantity: '',
    unit_price_sale: '',
    unit_price_purchase: ''
  });

  useEffect(() => {
    loadSupplies();
  }, []);

  const loadSupplies = async () => {
    try {
      const { data } = await suppliesApi.getAll();
      setSupplies(data);
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...formData,
        quantity: parseInt(formData.quantity),
        unit_price_sale: parseFloat(formData.unit_price_sale),
        unit_price_purchase: parseFloat(formData.unit_price_purchase)
      };
      
      if (selectedSupply) {
        await suppliesApi.update(selectedSupply.id, dataToSend);
      } else {
        await suppliesApi.create(dataToSend);
      }
      toast.success(t('success'));
      setDialogOpen(false);
      resetForm();
      loadSupplies();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const resetForm = () => {
    setSelectedSupply(null);
    setFormData({
      designation: '',
      quantity: '',
      unit_price_sale: '',
      unit_price_purchase: ''
    });
  };

  const handleEdit = (supply) => {
    setSelectedSupply(supply);
    setFormData({
      designation: supply.designation || '',
      quantity: supply.quantity?.toString() || '',
      unit_price_sale: supply.unit_price_sale?.toString() || '',
      unit_price_purchase: supply.unit_price_purchase?.toString() || ''
    });
    setDialogOpen(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' FCFA';
  };

  const totals = supplies.reduce((acc, s) => ({
    totalSale: acc.totalSale + (s.total_sale || 0),
    totalPurchase: acc.totalPurchase + (s.total_purchase || 0)
  }), { totalSale: 0, totalPurchase: 0 });

  return (
    <div className="space-y-6" data-testid="supplies-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="font-outfit text-2xl font-bold text-[#0F172A]">{t('supplies')}</h1>
        <Button
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="bg-[#0052FF] hover:bg-[#0043D1]"
          data-testid="add-supply-btn"
        >
          <Plus size={18} className="mr-2" />
          {t('addSupply')}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border border-[#E2E8F0] shadow-none">
          <CardContent className="p-4">
            <p className="font-manrope text-sm text-[#64748B]">{language === 'fr' ? 'Total Achats' : 'Total Purchases'}</p>
            <p className="font-outfit text-xl font-bold text-[#EF4444]">
              {formatCurrency(totals.totalPurchase)}
            </p>
          </CardContent>
        </Card>
        <Card className="border border-[#E2E8F0] shadow-none">
          <CardContent className="p-4">
            <p className="font-manrope text-sm text-[#64748B]">{language === 'fr' ? 'Total Ventes' : 'Total Sales'}</p>
            <p className="font-outfit text-xl font-bold text-[#10B981]">
              {formatCurrency(totals.totalSale)}
            </p>
          </CardContent>
        </Card>
        <Card className="border border-[#E2E8F0] shadow-none">
          <CardContent className="p-4">
            <p className="font-manrope text-sm text-[#64748B]">{language === 'fr' ? 'Marge' : 'Margin'}</p>
            <p className="font-outfit text-xl font-bold text-[#0052FF]">
              {formatCurrency(totals.totalSale - totals.totalPurchase)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-[#E2E8F0]">
                <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('date')}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('designation')}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A] text-right">{t('quantity')}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A] text-right">{t('unitPricePurchase')}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A] text-right">{t('unitPriceSale')}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A] text-right">{t('total')}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A] text-right">{t('actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0052FF] mx-auto" />
                  </TableCell>
                </TableRow>
              ) : supplies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-[#64748B]">{t('noData')}</TableCell>
                </TableRow>
              ) : (
                supplies.map((supply) => (
                  <TableRow key={supply.id} className="border-[#E2E8F0] hover:bg-[#F1F5F9]">
                    <TableCell className="font-manrope">{supply.date}</TableCell>
                    <TableCell className="font-manrope">{supply.designation}</TableCell>
                    <TableCell className="font-manrope text-right">{supply.quantity}</TableCell>
                    <TableCell className="font-manrope text-right">{formatCurrency(supply.unit_price_purchase)}</TableCell>
                    <TableCell className="font-manrope text-right">{formatCurrency(supply.unit_price_sale)}</TableCell>
                    <TableCell className="font-manrope font-semibold text-right">{formatCurrency(supply.total_sale)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(supply)}
                        className="hover:bg-[#EFF6FF] text-[#0052FF]"
                      >
                        <PencilSimple size={18} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Supply Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">
              {selectedSupply ? (language === 'fr' ? 'Modifier fourniture' : 'Edit Supply') : t('addSupply')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="font-manrope">{t('designation')}</Label>
              <Input
                value={formData.designation}
                onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                required
                className="border-[#E2E8F0]"
                data-testid="supply-designation"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-manrope">{t('quantity')}</Label>
              <Input
                type="number"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                required
                className="border-[#E2E8F0]"
                data-testid="supply-quantity"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-manrope">{t('unitPricePurchase')} (FCFA)</Label>
              <Input
                type="number"
                value={formData.unit_price_purchase}
                onChange={(e) => setFormData({ ...formData, unit_price_purchase: e.target.value })}
                required
                className="border-[#E2E8F0]"
                data-testid="supply-purchase-price"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-manrope">{t('unitPriceSale')} (FCFA)</Label>
              <Input
                type="number"
                value={formData.unit_price_sale}
                onChange={(e) => setFormData({ ...formData, unit_price_sale: e.target.value })}
                required
                className="border-[#E2E8F0]"
                data-testid="supply-sale-price"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('cancel')}</Button>
              <Button type="submit" className="bg-[#0052FF] hover:bg-[#0043D1]" data-testid="save-supply-btn">{t('save')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Supplies;
